/**
 * An error indicating that get or set of http request failed because it has not yet been
 * initialized.
 */
export class HttpRequestNotLoadedError extends Error {
	public readonly tag = "http-request-not-loaded-error" as const;
	constructor() {
		super("Get or set of http request failed because it has not yet been initialized");
		this.name = "HttpRequestNotLoadedError";
	}
}

/**
 * An error indicating that we failed to update the http request because it is halted.
 */
export class HttpRequestUpdateHaltedError extends Error {
	public readonly tag = "http-request-update-halted-error" as const;
	constructor(error: unknown) {
		const msg = error instanceof Error ? error.message : "unknown reason";
		super(`Failed to update http request because it is halted: ${msg}`);
		this.name = "HttpRequestUpdateHaltedError";
	}
}

/**
 * An error indicating that we failed to persist the http request to disk.
 */
export class HttpRequestPersistError extends Error {
	public readonly tag = "http-request-persist-error" as const;
	constructor(error: unknown) {
		const msg = error instanceof Error ? error.message : "unknown reason";
		super(`Failed to persist http request to disk: ${msg}`);
		this.name = "HttpRequestPersistError";
	}
}

/**
 * An error indicating that we failed to load the http request.
 */
export class HttpRequestLoadError extends Error {
	public readonly tag = "http-request-load-error" as const;
	constructor(error: unknown) {
		const msg =
			typeof error === "string"
				? error
				: error instanceof Error
					? error.message
					: "unknown reason";
		super(`Failed to load http request: ${msg}`);
		this.name = "HttpRequestLoadError";
	}
}

/**
 * An error indicating that our subscription to the collaboration server has terminated
 * unexpectedly.
 */
export class HttpRequestCollabSubscriptionError extends Error {
	public readonly tag = "http-request-collab-subscription-error" as const;
	constructor(error: unknown) {
		const msg =
			error instanceof Error ? error.message : "Subscription failed for unknown reason";
		super(`Subscription failed unexpectedly: ${msg}`);
		this.name = "HttpRequestCollabSubscriptionError";
	}
}

/**
 * An error indicating that HTTP request is misconfigured.
 *
 * The main use for this is to indicate that HTTP request is not configured to write to either
 * collab or disk, which means updates would be lost.
 */
export class HttpRequestMisconfiguredError extends Error {
	public readonly tag = "http-request-misconfigured-error" as const;
	constructor(message: string) {
		super(message);
		this.name = "HttpRequestMisconfiguredError";
	}
}

/**
 * An error indicating that the HTTP request data failed validation.
 */
export class HttpRequestValidationError extends Error {
	public readonly tag = "http-request-validation-error" as const;
	constructor(error: unknown) {
		const msg = error instanceof Error ? error.message : "unknown reason";
		super(`Invalid HTTP request data: ${msg}`);
		this.name = "HttpRequestValidationError";
	}
}
